interface AudioMetrics {
    inbound?: {
        packetsReceived?: number;
        bytesReceived?: number;
        packetsLost?: number;
        jitter?: number;
    };
    outbound?: {
        packetsSent?: number;
        bytesSent?: number;
    };
    codec?: string;
}

interface VideoMetrics {
    inbound?: {
        packetsReceived?: number;
        bytesReceived?: number;
        packetsLost?: number;
        framesDecoded?: number;
        frameWidth?: number;
        frameHeight?: number;
    };
    outbound?: {
        packetsSent?: number;
        bytesSent?: number;
        framesEncoded?: number;
    };
    codec?: string;
}

interface ConnectionMetrics {
    currentRoundTripTime?: number;
    totalRoundTripTime?: number;
    availableOutgoingBitrate?: number;
    bytesReceived?: number;
    bytesSent?: number;
    state?: RTCStatsIceCandidatePairState;
}

interface CandidateInfo {
    candidateType?: string;
    ip?: string;
    port?: number;
    protocol?: string;
}

interface WebRTCMetrics {
    audio: AudioMetrics;
    video: VideoMetrics;
    connection: ConnectionMetrics;
    candidates: {
        local: CandidateInfo[];
        remote: CandidateInfo[];
    };
}

async function collectWebRTCMetrics(peerConnection: RTCPeerConnection): Promise<WebRTCMetrics> {
    const stats = await peerConnection.getStats();

    const metrics: WebRTCMetrics = {
        audio: {},
        video: {},
        connection: {},
        candidates: { local: [], remote: [] },
    };

    stats.forEach((report) => {
        if (report.type === 'inbound-rtp' && 'kind' in report) {
            const inboundReport = report as RTCInboundRtpStreamStats;
            if (inboundReport.kind === 'audio') {
                metrics.audio.inbound = {
                    packetsReceived: inboundReport.packetsReceived,
                    bytesReceived: inboundReport.bytesReceived,
                    packetsLost: inboundReport.packetsLost,
                    jitter: inboundReport.jitter,
                };
            } else if (inboundReport.kind === 'video') {
                metrics.video.inbound = {
                    packetsReceived: inboundReport.packetsReceived,
                    bytesReceived: inboundReport.bytesReceived,
                    packetsLost: inboundReport.packetsLost,
                    framesDecoded: inboundReport.framesDecoded,
                    frameWidth: inboundReport.frameWidth,
                    frameHeight: inboundReport.frameHeight,
                };
            }
        } else if (report.type === 'outbound-rtp' && 'kind' in report) {
            const outboundReport = report as RTCOutboundRtpStreamStats;
            if (outboundReport.kind === 'audio') {
                metrics.audio.outbound = {
                    packetsSent: outboundReport.packetsSent,
                    bytesSent: outboundReport.bytesSent,
                };
            } else if (outboundReport.kind === 'video') {
                metrics.video.outbound = {
                    packetsSent: outboundReport.packetsSent,
                    bytesSent: outboundReport.bytesSent,
                    framesEncoded: outboundReport.framesEncoded,
                };
            }
        } else if (report.type === 'candidate-pair' && (report as RTCIceCandidatePairStats).state === 'succeeded') {
            const candidatePairReport = report as RTCIceCandidatePairStats;
            metrics.connection = {
                currentRoundTripTime: candidatePairReport.currentRoundTripTime,
                totalRoundTripTime: candidatePairReport.totalRoundTripTime,
                availableOutgoingBitrate: candidatePairReport.availableOutgoingBitrate,
                bytesReceived: candidatePairReport.bytesReceived,
                bytesSent: candidatePairReport.bytesSent,
                state: candidatePairReport.state,
            };
        } else if (report.type === 'local-candidate' || report.type === 'remote-candidate') {
            const candidateReport = report as RTCStats;
            const candidateInfo: CandidateInfo = {
                candidateType: 'candidateType' in candidateReport ? candidateReport.candidateType as string : undefined,
                ip: 'ip' in candidateReport ? candidateReport.ip as string : undefined,
                port: 'port' in candidateReport ? candidateReport.port as number : undefined,
                protocol: 'protocol' in candidateReport ? candidateReport.protocol as string : undefined,
            };
            if (report.type === 'local-candidate') {
                metrics.candidates.local.push(candidateInfo);
            } else {
                metrics.candidates.remote.push(candidateInfo);
            }
        }
    });

    // Добавляем информацию о кодеках
    const transceivers = peerConnection.getTransceivers();
    for (const transceiver of transceivers) {
        if (transceiver.sender.track) {
            const capabilities = RTCRtpSender.getCapabilities(transceiver.sender.track.kind);
            if (capabilities && capabilities.codecs && capabilities.codecs.length > 0) {
                if (transceiver.sender.track.kind === 'audio') {
                    metrics.audio.codec = capabilities.codecs[0].mimeType;
                } else if (transceiver.sender.track.kind === 'video') {
                    metrics.video.codec = capabilities.codecs[0].mimeType;
                }
            }
        }
    }

    return metrics;
}

// Пример использования
async function logWebRTCMetrics(peerConnection: RTCPeerConnection): Promise<WebRTCMetrics | undefined> {
    try {
        return await collectWebRTCMetrics(peerConnection);
    } catch (error) {
        console.error('Error collecting WebRTC metrics:', error);
        return undefined;

    }
}

export { logWebRTCMetrics };